/** @jsx jsx */
import * as React from 'react';

import { css, jsx } from '@compiled/react';
import debounce from 'lodash/debounce';
import { USER_TYPING_DEBOUNCE_TIME } from '@helpcenter/constants';
import type { FieldFormProps } from '@helpcenter/field-types';
import { FieldLoadingSkeleton } from '@helpcenter/loading';
import AsyncRichTextEditorV2 from '@helpcenter/rich-text-editor-v2';
import type { MediaContext, RichTextEditorActions } from '@helpcenter/rich-text-editor-v2';
import { FieldBase } from 'view/common/fields/field-base';
import type { FieldConfigs } from 'view/common/mention-provider';
import { withControlledFormComponentValue } from 'view/common/with-controlled-form-component-value';
import type { JSONDocNode } from '@atlaskit/editor-json-transformer/types';
import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';
import { token } from '@atlaskit/tokens';
import { isOnlyWhitespaceAdf } from '@atlassian/help-center-common-util/adf';
import { trackError } from '@atlassian/help-center-common-util/analytics';
interface Props extends FieldFormProps<string> {
	defaultValue?: string;
	value?: string;
	fieldConfigs?: FieldConfigs;
	mediaContext?: MediaContext;
	shouldReplaceValue?: boolean;
}

class RichTextArea extends React.Component<Props> {
	private editorActions?: RichTextEditorActions;

	componentDidUpdate(prevProps: Props) {
		// Update editor content when value changes and UI modifications are enabled
		if (
			this.props.shouldReplaceValue &&
			prevProps.value !== this.props.value &&
			this.editorActions &&
			this.props.value !== undefined
		) {
			this.editorActions.replaceDocument(this.props.value);
		}
	}

	handleInternalRender = (editorActions: RichTextEditorActions) => {
		this.editorActions = editorActions;
	};

	onFieldChange = (transformedDoc: JSONDocNode): void => {
		const { id, onChange } = this.props;

		if (onChange) {
			// When the fields content is cleared we reset it's value to an empty string in order to correctly display the dirty form warning
			const correctValue = isOnlyWhitespaceAdf(transformedDoc)
				? ''
				: JSON.stringify(transformedDoc);
			onChange(id, correctValue, false, { rawObject: {}, rawValue: correctValue });
		}
	};

	onFieldBlur = (event: React.FocusEvent<HTMLDivElement>) => {
		// Check if the focus is moving to an element within this component
		const currentTarget = event.currentTarget;
		const relatedTarget = event.relatedTarget as HTMLElement;

		// If relatedTarget is within the currentTarget, don't trigger blur
		if (relatedTarget && currentTarget.contains(relatedTarget)) {
			return;
		}

		if (this.props.onFieldBlurredByUser) {
			const { onFieldBlurredByUser } = this.props;
			// Handle the promise properly to avoid floating promise error
			this.editorActions
				?.getValue()
				.then((transformedDoc) => {
					const correctValue = isOnlyWhitespaceAdf(transformedDoc)
						? ''
						: JSON.stringify(transformedDoc);
					onFieldBlurredByUser({
						field: this.props.id,
						value: correctValue,
						additionalFieldData: {
							rawObject: {},
							rawValue: correctValue,
						},
					});
				})
				.catch((error) => {
					trackError(
						'rich.text.area.failed.to.get.value',
						{ message: JSON.stringify(error) },
						`Rich text area: Failed to get value from editor`,
					);
				});
		}
	};

	onFieldChangeDebounced = debounce(this.onFieldChange, USER_TYPING_DEBOUNCE_TIME);

	render() {
		const {
			id,
			label,
			required,
			errorMessage,
			description,
			disabled,
			defaultValue,
			fieldConfigs,
			value,
		} = this.props;
		const editorProps = FeatureGates.checkGate('request_create_inline_attachment')
			? {
					mediaContext: this.props.mediaContext,
				}
			: {};
		const updatedDefaultValue = defaultValue ?? value;
		return (
			<FieldBase
				label={label}
				description={description}
				htmlFor={id}
				required={required}
				errorMessage={errorMessage}
			>
				<div
					id={id}
					css={[wrapperStyles, errorMessage ? errorStyles : noErrorStyles]}
					data-test-id="rich-text-area-field"
					aria-label={label}
					onBlur={this.onFieldBlur}
				>
					<AsyncRichTextEditorV2
						isExpanded
						editorType="field"
						isEmojiEnabled
						disabled={!!disabled}
						onChange={this.onFieldChangeDebounced}
						defaultValue={updatedDefaultValue}
						fieldConfigs={fieldConfigs}
						onInternalRender={this.handleInternalRender}
						// TODO: editor currently does not support aria-labelledby, so we are using ariaLabel for now
						ariaLabel={label}
						ariaDescribedby={`${id}-field-description`}
						LoadingComponent={FieldLoadingSkeleton}
						{...editorProps}
					/>
				</div>
			</FieldBase>
		);
	}
}

export default RichTextArea;

const ControlledRichTextArea = withControlledFormComponentValue(
	(_field: string, value: string) => value,
)(RichTextArea);

export const FormRichTextArea = ControlledRichTextArea;

// Manual overrides required to theme the editor to look like a request create form field
const wrapperStyles = css({
	flex: '1 1 100%',
	// Nested selectors will be addressed in REQIN-2475
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'.akEditor': {
		borderRadius: token('radius.medium', '6px'),
		border: `${token('border.width', '1px')} solid`,
		borderColor: token('color.border.input', '#8c8f97'),
		backgroundColor: token('color.background.input', '#FAFBFC'),
		transition: 'background-color 200ms ease-in-out, border-color 200ms ease-in-out',
	},
	// Nested selectors will be addressed in REQIN-2475
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'.akEditor + div': {
		padding: 0,
	},
	// Nested selectors will be addressed in REQIN-2475
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'.akEditor > div': {
		backgroundColor: token('color.background.input', '#FAFBFC'),
		transition: 'background-color 200ms ease-in-out, border-color 200ms ease-in-out',
		borderRadius: token('radius.medium', '6px'),
	},
	// Nested selectors will be addressed in REQIN-2475
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'&:hover .akEditor': {
		backgroundColor: token('color.background.input.hovered', '#EBECF0'),
	},
	// Nested selectors will be addressed in REQIN-2475
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'&:hover .akEditor > div': {
		backgroundColor: token('color.background.input.hovered', '#EBECF0'),
	},
	// Nested selectors will be addressed in REQIN-2475
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'&:focus-within .akEditor': {
		backgroundColor: token('color.background.input', '#FFFFFF'),
		borderColor: token('color.border.focused', '#4C9AFF'),
		outline: `${token('border.width', '1px')} solid ${token('color.border.focused', '#4C9AFF')}`,
	},
	// Nested selectors will be addressed in REQIN-2475
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'&:focus-within .akEditor > div': {
		backgroundColor: token('color.background.input', '#FFFFFF'),
	},
	// Nested selectors will be addressed in REQIN-2475
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'&:focus-within:hover .akEditor': {
		backgroundColor: token('color.background.input', '#FFFFFF'),
		borderColor: token('color.border.focused', '#4C9AFF'),
		outline: `${token('border.width', '1px')} solid ${token('color.border.focused', '#4C9AFF')}`,
	},
	// Nested selectors will be addressed in REQIN-2475
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'&:focus-within:hover .akEditor > div': {
		backgroundColor: token('color.background.input', '#FFFFFF'),
	},
});

const errorStyles = css({
	// Nested selectors will be addressed in REQIN-2475
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'.akEditor': {
		borderColor: token('color.border.danger', '#BF2600'),
		outline: `${token('border.width', '1px')} solid ${token('color.border.danger', '#BF2600')}`,
	},
});

const noErrorStyles = css({
	// Nested selectors will be addressed in REQIN-2475
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'.akEditor': {
		borderColor: token('color.border.input', '#42526E'),
		outline: 'none',
	},
});
